<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { NButton, NSpin } from 'naive-ui'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useThreadsStore, type ThreadMessage } from '../stores/threads'
import { useWikiMarkdown, type CitationRef } from '../composables/useWikiMarkdown'
import { useInternalLinkClicks } from '../composables/useInternalLinkClicks'
import CitationHoverManager from './CitationHoverManager.vue'

// The message-bubbles half of ThreadDrawer.vue (#1102) — rendering,
// citation lookups, and the weak-citation nudge for the current thread's
// messages. Extracted so the parent stays under the 300-line hard limit;
// no behavior change from the inline version. Only needs the current
// thread's messages, the submitting flag, and the wiki-pages catalog to
// render — the composer, thread-submit lifecycle, and recent-threads list
// stay in the parent.
const props = defineProps<{
  messages: ThreadMessage[]
  submitting: boolean
  elapsedSeconds: number
}>()

const emit = defineEmits<{
  (e: 'open-citation', path: string): void
  (e: 'close'): void
  (e: 'add-source'): void
}>()

const wikiPages = useWikiPagesStore()
const threads = useThreadsStore()
const { pages } = storeToRefs(wikiPages)
const wikiMarkdown = useWikiMarkdown()
const bubblesEl = ref<HTMLElement | null>(null)

// Keep the bubble list scrolled to the bottom as new turns land.
watch(
  () => props.messages.length,
  async () => {
    await nextTick()
    const el = bubblesEl.value
    if (el) el.scrollTop = el.scrollHeight
  },
)

// Renders an assistant turn with inline `[n]` citation markers anchored
// to the citations array. User turns flow through plain `render` because
// they're verbatim input — no citations to inject.
function renderAssistant(content: string, citations: readonly CitationRef[]): string {
  return wikiMarkdown.renderWithCitations(content, 'index.md', enrichWithTitles(citations))
}

function renderUser(content: string): string {
  return wikiMarkdown.render(content, 'index.md')
}

// Look up each citation in the catalog so the inline `[n]` marker's
// title attribute renders as a human-readable name on hover. Falls back
// to the path when the catalog hasn't loaded yet or the cited page is
// no longer present.
function enrichWithTitles(citations: readonly CitationRef[]): CitationRef[] {
  return citations.map((c) => ({
    path: c.path,
    title: c.title || pages.value.find((p) => p.path === c.path)?.title || undefined,
  }))
}

// Citation chips render as the page title rather than the raw markdown
// path: `Anthropic` reads cleaner than `entities/anthropic.md` and is
// what the user knows the page as. Falls back to the path when the
// catalog hasn't loaded yet or the cited page isn't (or is no longer) in
// the index — both edge cases keep the chip useful instead of blanking.
function citationLabel(path: string): string {
  return pages.value.find((p) => p.path === path)?.title || path
}

function openCitation(path: string) {
  emit('open-citation', path)
}

const onBubbleClick = useInternalLinkClicks(() => emit('close'))
</script>

<template>
  <!-- Thread bubbles: scrolls independently so the composer stays
       pinned. Wrapped in CitationHoverManager so any [n] citation
       marker rendered into an assistant bubble gets a hover-card
       preview without per-bubble wiring. -->
  <CitationHoverManager>
    <div
      ref="bubblesEl"
      class="flex flex-col gap-3 mb-4 max-h-[60vh] overflow-y-auto pr-1"
      data-testid="thread-bubbles"
      aria-live="polite"
      @click="onBubbleClick"
    >
      <div
        v-for="(msg, idx) in props.messages"
        :key="idx"
        class="flex"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          class="max-w-[92%] rounded-xl px-3 py-2"
          :class="
            msg.role === 'user'
              ? 'bg-brand-primary/10 border border-brand-primary/30 text-white'
              : 'bg-white/5 border border-white/10 text-white/90'
          "
        >
          <article
            class="wiki-markdown text-sm inline"
            :class="{ 'opacity-80': msg.role === 'user' }"
            v-html="
              msg.role === 'assistant' ? renderAssistant(msg.content, msg.citations || []) : renderUser(msg.content)
            "
          />
          <span
            v-if="submitting && msg.role === 'assistant' && idx === props.messages.length - 1"
            class="inline-block w-1.5 h-4 ml-0.5 align-text-bottom rounded-sm bg-brand-primary/70 animate-pulse"
            data-testid="thread-streaming-cursor"
            aria-hidden="true"
          />
          <div
            v-if="msg.role === 'assistant' && msg.citations && msg.citations.length"
            class="mt-2 flex flex-wrap gap-1 items-center"
          >
            <span class="text-[10px] uppercase tracking-widest text-white/30 font-semibold mr-1">Sources</span>
            <NButton
              v-for="(c, idx) in msg.citations"
              :key="c.path"
              text
              type="primary"
              class="!text-xs"
              :title="c.path"
              @click="openCitation(c.path)"
              >[{{ idx + 1 }}] {{ citationLabel(c.path) }}</NButton
            >
          </div>

          <!-- Weak-citation nudge: a thinly-covered answer offers
               an in-flow path to close the gap. Hidden while this turn
               is still streaming so it only appears on the settled
               answer. -->
          <div
            v-if="msg.role === 'assistant' && msg.weak_citation && !(submitting && idx === props.messages.length - 1)"
            class="mt-2 flex flex-wrap items-center gap-2 text-xs text-amber-300/80"
            data-testid="weak-citation-nudge"
          >
            <span>The wiki had limited coverage here.</span>
            <NButton
              size="tiny"
              type="warning"
              ghost
              data-testid="weak-citation-add-source"
              @click="emit('add-source')"
            >
              Add a source
            </NButton>
          </div>
        </div>
      </div>
      <div
        v-if="submitting"
        class="flex items-center gap-2 text-white/50 text-xs"
        data-testid="thread-thinking"
        role="status"
      >
        <NSpin size="small" />
        <span>Thinking… {{ elapsedSeconds }}s</span>
        <NButton size="tiny" quaternary type="warning" data-testid="thread-cancel" @click="threads.cancel"
          >Cancel</NButton
        >
      </div>
    </div>
  </CitationHoverManager>
</template>
